import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { isMobileDevice } from "../utils/isMobileDevice";
import { isPlayerAccount, readActiveHomeRole } from "../utils/homeRole";
import {
  getActiveLiveTrainingContext,
  resolveGroupIdsForLiveTraining,
} from "./useLiveTrainingRedirect";
import { isWithinSessionWindow, isSessionFinalized } from "../utils/liveTrainingSessionWindow";

/**
 * FAB visibility for returning to live training from dashboard/home.
 * Live Training is mobile-only.
 */
export function useLiveTrainingReturnFab() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState("");
  const [sessionState, setSessionState] = useState(null);

  const refresh = useCallback(async () => {
    if (!isMobileDevice()) {
      setVisible(false);
      return;
    }
    if (location.pathname === "/live-training") {
      setVisible(false);
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const permissions = user.permissions || [];
    if (isPlayerAccount(user, { homeRole: readActiveHomeRole(user) })) {
      setVisible(false);
      return;
    }

    try {
      const groupIds = await resolveGroupIdsForLiveTraining(user);
      if (!groupIds.length) {
        setVisible(false);
        return;
      }

      const ctx = await getActiveLiveTrainingContext({ user, groupIds, permissions });
      if (
        !ctx ||
        isSessionFinalized(ctx.session) ||
        !isWithinSessionWindow(ctx.session)
      ) {
        setVisible(false);
        return;
      }
      const num = ctx.session.ts_session_number;
      setLabel(num ? `#${num}` : "");
      setSessionState(ctx.session);
      setVisible(true);
    } catch {
      setVisible(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 60000);
    return () => window.clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const onStatus = () => refresh();
    window.addEventListener("smarttt-session-live-status", onStatus);
    return () => window.removeEventListener("smarttt-session-live-status", onStatus);
  }, [refresh]);

  return { visible, label, sessionState, refresh };
}
